import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings as SettingsIcon, User, Palette, Shield, Bell, Database, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AccountSection,
  AppearanceSection,
  PrivacySection,
  NotificationSection,
  DataSection,
  DangerZoneSection,
} from "@/components/Settings";

const Settings = () => {
  const { user, userProfile, updateProfile, signOut } = useAuth();
  
  const [privacySettings, setPrivacySettings] = useState({
    hideComments: false,
    hideLikes: false,
    hidePosts: false,
    privateProfile: false,
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    notifyOnComments: true,
    notifyOnLikes: true,
    notifyOnSaves: true,
  });
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('hide_comments, hide_likes, hide_posts, private_profile, email_notifications, push_notifications, notify_on_comments, notify_on_likes, notify_on_saves')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setPrivacySettings({
            hideComments: data.hide_comments || false,
            hideLikes: data.hide_likes || false,
            hidePosts: data.hide_posts || false,
            privateProfile: data.private_profile || false,
          });
          setNotificationSettings({
            emailNotifications: data.email_notifications ?? true,
            pushNotifications: data.push_notifications ?? true,
            notifyOnComments: data.notify_on_comments ?? true,
            notifyOnLikes: data.notify_on_likes ?? true,
            notifyOnSaves: data.notify_on_saves ?? true,
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [user]);

  const handlePrivacyChange = async (setting: string, value: boolean) => {
    if (!user) return;

    const dbKey = setting.replace(/([A-Z])/g, '_$1').toLowerCase();
    
    setPrivacySettings(prev => ({ ...prev, [setting]: value }));

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [dbKey]: value })
        .eq('id', user.id);

      if (error) throw error;
      toast.success('Setting updated');
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
      setPrivacySettings(prev => ({ ...prev, [setting]: !value }));
    }
  };

  const handleNotificationChange = async (setting: string, value: boolean) => {
    if (!user) return;

    const dbKey = setting.replace(/([A-Z])/g, '_$1').toLowerCase();
    
    setNotificationSettings(prev => ({ ...prev, [setting]: value }));

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [dbKey]: value })
        .eq('id', user.id);

      if (error) throw error;
      toast.success('Setting updated');
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
      setNotificationSettings(prev => ({ ...prev, [setting]: !value }));
    }
  };

  const handleAvatarSelect = async (avatarId: number) => {
    if (!userProfile) return;
    
    try {
      await updateProfile({ ...userProfile, avatarId });
      toast.success('Avatar updated');
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error('Failed to update avatar');
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <Card className="border-border/50 bg-card/50">
            <CardContent className="p-8 text-center">
              <SettingsIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg">Please sign in to access settings.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading || !userProfile) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <Card className="border-border/50 bg-card/50">
            <CardContent className="p-8 text-center">
              <div className="animate-pulse space-y-4">
                <div className="h-8 w-48 bg-muted rounded mx-auto" />
                <div className="h-4 w-64 bg-muted rounded mx-auto" />
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <SettingsIcon className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Settings</h1>
        </div>
        
        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 h-auto gap-1 bg-muted/50 p-1">
            <TabsTrigger value="account" className="flex items-center gap-2 text-xs sm:text-sm">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Account</span>
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-2 text-xs sm:text-sm">
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Appearance</span>
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2 text-xs sm:text-sm">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Privacy</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2 text-xs sm:text-sm">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-2 text-xs sm:text-sm">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">Data</span>
            </TabsTrigger>
            <TabsTrigger value="danger" className="flex items-center gap-2 text-xs sm:text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Danger</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account">
            <AccountSection user={user} userProfile={userProfile} />
          </TabsContent>

          <TabsContent value="appearance">
            <AppearanceSection
              avatarId={userProfile.avatarId}
              onAvatarSelect={handleAvatarSelect}
              isPremium={userProfile.isSubscriber}
            />
          </TabsContent>

          <TabsContent value="privacy">
            <PrivacySection
              settings={privacySettings}
              onSettingChange={handlePrivacyChange}
            />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationSection
              settings={notificationSettings}
              onSettingChange={handleNotificationChange}
            />
          </TabsContent>

          <TabsContent value="data">
            <DataSection userId={user.id} />
          </TabsContent>

          <TabsContent value="danger">
            <DangerZoneSection userId={user.id} onSignOut={signOut} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;
